import {
  getBearerTokenExpiresAt,
  getLandlordBearerTokenExpiresAt,
} from '@afinius/api-client/bearer-token';
import { afterResponseHooks, retryRequest } from '@afinius/api-client/client';
import { App } from '@afinius/api-client/landlord-application/types';
import type { ApiError } from '@afinius/types/Error';
import { getAppUrl } from '@afinius/utilities/app-url';
import {
  attemptSsoRedirect,
  classifySsoVerifyError,
  clearSsoRedirectAttempt,
  redirectToLogin,
  refreshLandlordToken,
  RefreshResult,
  refreshTenantToken,
  silentSso,
} from '@afinius/utilities/silent-sso';
import { onScopeDispose, ref } from 'vue';

const PROACTIVE_REFRESH_LEAD_MS = 60_000;
const PROACTIVE_REFRESH_POLL_MS = 30_000;

interface SessionStore {
  isAuthenticated: boolean;
  ssoVerify: (token: string) => Promise<unknown>;
}

const AUTH_HANDSHAKE_PATHS = [
  '/auth/sso/verify',
  '/auth/refresh',
  '/landlord/auth/sso/initiate',
  '/landlord/auth/refresh',
];

export const useSessionRefresh = (store: SessionStore, check: () => Promise<void>) => {
  const isLoading = ref(true);

  const authOrigin = getAppUrl(App.AfiniusAuth);
  const ssoInitiateUrl = getAppUrl(App.ApiLandlord, 'landlord/auth/sso/initiate');

  const tenantRefreshState: { promise: Promise<RefreshResult> | null } = { promise: null };
  const landlordRefreshState: { promise: Promise<RefreshResult> | null } = { promise: null };
  const ssoRefreshState: { promise: Promise<string | null> | null } = { promise: null };
  let lastRefreshWasUnavailable = false;

  const refreshLandlord = () => {
    if (!landlordRefreshState.promise) {
      landlordRefreshState.promise = refreshLandlordToken().finally(() => {
        landlordRefreshState.promise = null;
      });
    }

    return landlordRefreshState.promise;
  };

  const refreshTenant = () => {
    if (!tenantRefreshState.promise) {
      tenantRefreshState.promise = refreshTenantToken().finally(() => {
        tenantRefreshState.promise = null;
      });
    }

    return tenantRefreshState.promise;
  };

  const maybeProactivelyRefresh = () => {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    if (!store.isAuthenticated) return;

    const tenantExpiresAt = getBearerTokenExpiresAt();
    if (tenantExpiresAt !== null && tenantExpiresAt - Date.now() < PROACTIVE_REFRESH_LEAD_MS) {
      refreshTenant();
    }

    const landlordExpiresAt = getLandlordBearerTokenExpiresAt();
    if (landlordExpiresAt !== null && landlordExpiresAt - Date.now() < PROACTIVE_REFRESH_LEAD_MS) {
      refreshLandlord();
    }
  };

  const proactiveRefreshTimer = setInterval(maybeProactivelyRefresh, PROACTIVE_REFRESH_POLL_MS);
  document.addEventListener('visibilitychange', maybeProactivelyRefresh);

  onScopeDispose(() => {
    clearInterval(proactiveRefreshTimer);
    document.removeEventListener('visibilitychange', maybeProactivelyRefresh);
  });

  const verifySsoToken = async (token: string): Promise<boolean> => {
    try {
      await store.ssoVerify(token);
      clearSsoRedirectAttempt();
      isLoading.value = false;
      return true;
    } catch (error) {
      if (classifySsoVerifyError(error) === 'denied') {
        redirectToLogin(authOrigin, 'tenant-access-denied');
      } else if (!attemptSsoRedirect(authOrigin, ssoInitiateUrl)) {
        redirectToLogin(authOrigin, 'sso-failed');
      }

      return false;
    }
  };

  afterResponseHooks.push(async ({ request, response, retryCount }) => {
    if (response.status !== 401) return response;
    if (retryCount > 0) return response;
    if (AUTH_HANDSHAKE_PATHS.some((path) => request.url.includes(path))) return response;

    const isLandlordRequest = request.url.includes('/landlord/');
    let refreshUnavailable = false;

    const attemptRefresh = async (refresh: () => Promise<RefreshResult>) => {
      const result = await refresh();
      if (result === 'unavailable') refreshUnavailable = true;
      return result === 'recovered';
    };

    if (isLandlordRequest) {
      if (await attemptRefresh(refreshLandlord)) {
        return retryRequest();
      }
    } else {
      if (await attemptRefresh(refreshTenant)) {
        return retryRequest();
      }

      if (await attemptRefresh(refreshLandlord)) {
        if (await attemptRefresh(refreshTenant)) {
          return retryRequest();
        }
      }
    }

    lastRefreshWasUnavailable = refreshUnavailable;

    if (refreshUnavailable) {
      return response;
    }

    if (!ssoRefreshState.promise) {
      ssoRefreshState.promise = silentSso(authOrigin, ssoInitiateUrl)
        .then(async (token) => {
          if (!token) {
            if (!attemptSsoRedirect(authOrigin, ssoInitiateUrl)) {
              redirectToLogin(authOrigin, 'session-expired');
            }

            return null;
          }

          return (await verifySsoToken(token)) ? token : null;
        })
        .finally(() => {
          ssoRefreshState.promise = null;
        });
    }

    const token = await ssoRefreshState.promise;

    if (token) {
      return retryRequest();
    }

    return response;
  });

  const initializeSession = async () => {
    if (store.isAuthenticated) {
      lastRefreshWasUnavailable = false;
      let checkFailedWithoutResponse = false;

      const sessionValid = await check()
        .then(() => true)
        .catch((error) => {
          checkFailedWithoutResponse = (error as ApiError).response === undefined;
          return false;
        });

      if (sessionValid) {
        isLoading.value = false;
        return;
      }

      if (checkFailedWithoutResponse || lastRefreshWasUnavailable) {
        isLoading.value = false;
        return;
      }

      if ((await refreshLandlord()) === 'recovered') {
        const recovered = await check()
          .then(() => true)
          .catch(() => false);

        if (recovered) {
          isLoading.value = false;
          return;
        }
      }
    }

    const token = await silentSso(authOrigin, ssoInitiateUrl);

    if (token) {
      await verifySsoToken(token);
    }
  };

  return {
    isLoading,
    initializeSession,
  };
};
